import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Search, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import WhisperIcon from './WhisperIcon';
import ArtistNotificationBell from './ArtistNotificationBell';

const FriendsModal = ({ isOpen, onClose, user, onWhisperClick }) => {
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [following, setFollowing] = useState([]);
  const [artistFollowing, setArtistFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mouseX, setMouseX] = useState(0);
  const dockRef = useRef(null);
  const navigate = useNavigate();

  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  
  // Mock avatars for testing (gradient colors)
  const mockAvatars = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
    'linear-gradient(135deg, #f77062 0%, #fe5196 100%)',
    'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)',
    'linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%)',
    'linear-gradient(135deg, #feada6 0%, #f5efef 100%)',
  ];

  // Load all data when modal opens (not just active tab)
  useEffect(() => {
    if (isOpen && user?.user_id) {
      loadFriends();
      loadFollowing();
      loadFollowers();
    }
  }, [isOpen, user]);
  
  // Separate artists from regular following
  useEffect(() => {
    const artists = following.filter(f => ['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase()));
    setArtistFollowing(artists);
  }, [following]);
  
  // Update filtered friends for search
  useEffect(() => {
    if (activeTab === 'search') {
      setFilteredFriends(friends);
    }
  }, [activeTab, friends]);
  
  // Real-time search across ALL categories (friends, following, followers)
  useEffect(() => {
    if (activeTab === 'search') {
      // Combine all users from all categories
      const allUsers = [...friends, ...following, ...followers];
      
      // Remove duplicates based on id
      const uniqueUsers = allUsers.filter((user, index, self) =>
        index === self.findIndex((u) => (u.id || u.user_id) === (user.id || user.user_id))
      );
      
      if (searchQuery.trim()) {
        const filtered = uniqueUsers.filter(user => 
          user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredFriends(filtered);
      } else {
        setFilteredFriends(uniqueUsers);
      }
    }
  }, [searchQuery, friends, following, followers, activeTab]);

  const loadFriends = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/users/${user.user_id}/friends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Add mock friends for testing if list is small
        const realFriends = Array.isArray(data) ? data : [];
        const mockFriends = [
          { id: 'mock1', name: 'Sarah Martinez', email: 'sarah.m@beatseek.com', mockAvatar: mockAvatars[0] },
          { id: 'mock2', name: 'DJ Phoenix', email: 'phoenix@beatseek.com', mockAvatar: mockAvatars[1] },
          { id: 'mock3', name: 'Luna Waves', email: 'luna@beatseek.com', mockAvatar: mockAvatars[2] },
          { id: 'mock4', name: 'Marcus Bass', email: 'marcus@beatseek.com', mockAvatar: mockAvatars[3] },
          { id: 'mock5', name: 'Nina Melody', email: 'nina@beatseek.com', mockAvatar: mockAvatars[4] },
          { id: 'mock6', name: 'Kai Rhythm', email: 'kai@beatseek.com', mockAvatar: mockAvatars[5] },
          { id: 'mock7', name: 'Zoe Beats', email: 'zoe@beatseek.com', mockAvatar: mockAvatars[6] },
          { id: 'mock8', name: 'Max Tempo', email: 'max@beatseek.com', mockAvatar: mockAvatars[7] },
          { id: 'mock9', name: 'Aria Sound', email: 'aria@beatseek.com', mockAvatar: mockAvatars[8] },
          { id: 'mock10', name: 'Felix Drop', email: 'felix@beatseek.com', mockAvatar: mockAvatars[9] },
          { id: 'mock11', name: 'Maya Chen', email: 'maya.chen@beatseek.com', mockAvatar: mockAvatars[10] },
          { id: 'mock12', name: 'Rio Vibe', email: 'rio@beatseek.com', mockAvatar: mockAvatars[11] },
          { id: 'mock13', name: 'DJ Stellar', email: 'djstellar@beatseek.com', mockAvatar: mockAvatars[12] },
          { id: 'mock14', name: 'DJ Nova', email: 'djnova@beatseek.com', mockAvatar: mockAvatars[13] },
        ];
        
        const combinedFriends = [...realFriends, ...mockFriends];
        console.log('🎵 Loading Friends:', {
          realFriends: realFriends.length,
          mockFriends: mockFriends.length,
          combined: combinedFriends.length,
          friends: combinedFriends
        });
        
        setFriends(combinedFriends);
      } else {
        console.error('❌ Friends API failed:', response.status);
        // Still set mock friends even if API fails
        const mockFriends = [
          { id: 'mock1', name: 'Sarah Martinez', email: 'sarah.m@beatseek.com', mockAvatar: mockAvatars[0] },
          { id: 'mock2', name: 'DJ Phoenix', email: 'phoenix@beatseek.com', mockAvatar: mockAvatars[1] },
          { id: 'mock3', name: 'Luna Waves', email: 'luna@beatseek.com', mockAvatar: mockAvatars[2] },
          { id: 'mock4', name: 'Marcus Bass', email: 'marcus@beatseek.com', mockAvatar: mockAvatars[3] },
          { id: 'mock5', name: 'Nina Melody', email: 'nina@beatseek.com', mockAvatar: mockAvatars[4] },
          { id: 'mock6', name: 'Kai Rhythm', email: 'kai@beatseek.com', mockAvatar: mockAvatars[5] },
          { id: 'mock7', name: 'Zoe Beats', email: 'zoe@beatseek.com', mockAvatar: mockAvatars[6] },
          { id: 'mock8', name: 'Max Tempo', email: 'max@beatseek.com', mockAvatar: mockAvatars[7] },
          { id: 'mock9', name: 'Aria Sound', email: 'aria@beatseek.com', mockAvatar: mockAvatars[8] },
          { id: 'mock10', name: 'Felix Drop', email: 'felix@beatseek.com', mockAvatar: mockAvatars[9] },
          { id: 'mock11', name: 'Maya Chen', email: 'maya.chen@beatseek.com', mockAvatar: mockAvatars[10] },
          { id: 'mock12', name: 'Rio Vibe', email: 'rio@beatseek.com', mockAvatar: mockAvatars[11] },
          { id: 'mock13', name: 'DJ Stellar', email: 'djstellar@beatseek.com', mockAvatar: mockAvatars[12] },
          { id: 'mock14', name: 'DJ Nova', email: 'djnova@beatseek.com', mockAvatar: mockAvatars[13] },
        ];
        setFriends(mockFriends);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowing = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/users/${user.user_id}/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Add mock following for testing
        const realFollowing = Array.isArray(data) ? data : [];
        const mockFollowing = [
          { id: 'mockf1', name: 'TechnoKing', email: 'techno@beatseek.com', mockAvatar: mockAvatars[12] },
          { id: 'mockf2', name: 'HouseQueen', email: 'house@beatseek.com', mockAvatar: mockAvatars[13] },
          { id: 'mockf3', name: 'Trance Master', email: 'trance@beatseek.com', mockAvatar: mockAvatars[14] },
          { id: 'mockf4', name: 'Dubstep Pro', email: 'dubstep@beatseek.com', mockAvatar: mockAvatars[0] },
          { id: 'mockf5', name: 'DJ Electro', email: 'djelectro@beatseek.com', mockAvatar: mockAvatars[1] },
          { id: 'mockf6', name: 'Bass Wizard', email: 'bass@beatseek.com', mockAvatar: mockAvatars[2] },
          { id: 'mockf7', name: 'Synth Wave', email: 'synth@beatseek.com', mockAvatar: mockAvatars[3] },
          { id: 'mockf8', name: 'Deep House', email: 'deep@beatseek.com', mockAvatar: mockAvatars[4] },
        ];
        
        setFollowing([...realFollowing, ...mockFollowing]);
      }
    } catch (error) {
      console.error('Error loading following:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/users/${user.user_id}/followers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Add mock followers for testing
        const realFollowers = Array.isArray(data) ? data : [];
        const mockFollowers = [
          { id: 'mockfo1', name: 'Beat Hunter', email: 'hunter@beatseek.com', mockAvatar: mockAvatars[5] },
          { id: 'mockfo2', name: 'Track Seeker', email: 'seeker@beatseek.com', mockAvatar: mockAvatars[6] },
          { id: 'mockfo3', name: 'Music Lover', email: 'lover@beatseek.com', mockAvatar: mockAvatars[7] },
          { id: 'mockfo4', name: 'EDM Fan', email: 'edm@beatseek.com', mockAvatar: mockAvatars[8] },
          { id: 'mockfo5', name: 'Rave Spirit', email: 'rave@beatseek.com', mockAvatar: mockAvatars[9] },
          { id: 'mockfo6', name: 'Festival Goer', email: 'festival@beatseek.com', mockAvatar: mockAvatars[10] },
          { id: 'mockfo7', name: 'Club Regular', email: 'club@beatseek.com', mockAvatar: mockAvatars[11] },
          { id: 'mockfo8', name: 'DJ Follower', email: 'dj@beatseek.com', mockAvatar: mockAvatars[12] },
          { id: 'mockfo9', name: 'Sound Explorer', email: 'explorer@beatseek.com', mockAvatar: mockAvatars[13] },
          { id: 'mockfo10', name: 'Vibe Collector', email: 'vibe@beatseek.com', mockAvatar: mockAvatars[14] },
        ];
        
        setFollowers([...realFollowers, ...mockFollowers]);
      }
    } catch (error) {
      console.error('Error loading followers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate scale based on distance (Mac Dock effect) - Reduced to prevent overlap
  const calculateScale = (index, hoveredIdx) => {
    if (hoveredIdx === null) return 1;
    
    const distance = Math.abs(index - hoveredIdx);
    
    if (distance === 0) return 1.35; // Hovered item: 108px (80 * 1.35)
    if (distance === 1) return 1.2; // Neighbors: 96px (80 * 1.2)
    if (distance === 2) return 1.1; // Next neighbors: 88px (80 * 1.1)
    
    return 1; // Others: 80px
  };

  const renderDockList = (users, showWhisper = false) => {
    if (loading) {
      return <div className="text-center py-8 text-gray-400">Loading...</div>;
    }

    if (users.length === 0) {
      return <div className="text-center py-8 text-gray-400">No users found</div>;
    }

    return (
      <div 
        ref={dockRef}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-12 px-8 py-12"
        style={{ overflow: 'visible' }}
      >
        {users.map((person, index) => {
          const scale = calculateScale(index, hoveredIndex);
          const baseSize = 80;
          
          return (
            <div
              key={person.id || person.user_id}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="flex flex-col items-center transition-all duration-300 ease-out"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'center bottom',
                zIndex: hoveredIndex === index ? 10 : 1,
                // Fixed height to prevent layout shift when Whisper button appears
                minHeight: '140px',
              }}
            >
              {/* Avatar - Clickable to go to profile */}
              <div
                onClick={() => {
                  const userId = person.id || person.user_id;
                  navigate(`/profile/${userId}`);
                  onClose();
                }}
                className="rounded-full flex items-center justify-center text-white font-bold shadow-2xl border-4 border-white/20 hover:border-cyan-400/50 transition-all duration-300 cursor-pointer overflow-hidden"
                style={{
                  width: `${baseSize}px`,
                  height: `${baseSize}px`,
                  background: person.profile_picture ? 'transparent' : (person.mockAvatar || mockAvatars[index % mockAvatars.length] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                  boxShadow: hoveredIndex === index 
                    ? '0 20px 40px rgba(0, 255, 255, 0.4), 0 0 20px rgba(0, 255, 255, 0.3)' 
                    : '0 10px 20px rgba(0, 0, 0, 0.5)',
                }}
              >
                {person.profile_picture ? (
                  <img 
                    src={`${API.replace('/api', '')}${person.profile_picture.startsWith('/api') ? person.profile_picture : '/api' + person.profile_picture.replace('/uploads/', '/uploads/')}`}
                    alt={person.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background = mockAvatars[index % mockAvatars.length] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.target.parentElement.innerHTML = person.name?.charAt(0).toUpperCase() || '?';
                    }}
                  />
                ) : (
                  person.name?.charAt(0).toUpperCase() || '?'
                )}
              </div>
              
              {/* Username */}
              <div 
                className="mt-2 text-white font-semibold text-center transition-all duration-300"
                style={{
                  fontSize: hoveredIndex === index ? '14px' : '12px',
                  opacity: scale > 1 ? 1 : 0.8,
                }}
              >
                {person.name || person.from_user_name}
              </div>
              
              {/* Whisper button placeholder - ALWAYS rendered to prevent layout shift */}
              <div style={{ height: '32px', marginTop: '8px' }}>
                {showWhisper && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onWhisperClick({
                        id: person.id || person.user_id,
                        name: person.name || person.from_user_name
                      });
                      onClose();
                    }}
                    className="px-3 py-1 rounded-full bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs font-medium hover:shadow-lg hover:shadow-pink-500/30 transition-all flex items-center gap-1"
                    style={{
                      opacity: hoveredIndex === index ? 1 : 0,
                      pointerEvents: hoveredIndex === index ? 'auto' : 'none',
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    <WhisperIcon size={14} />
                    Whisper
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  // Tab gradient colors
  const tabColors = {
    friends: 'from-cyan-600 to-blue-600',
    following: 'from-green-500 to-emerald-500',
    artistFollowing: 'from-amber-500 to-yellow-500',
    followers: 'from-orange-500 to-yellow-500',
    search: 'from-purple-500 to-pink-500'
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border-2 border-cyan-500/50 shadow-2xl w-full max-w-[90vw] max-h-[90vh] flex flex-col overflow-hidden relative">
        {/* Animated background effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-pink-500/5 animate-pulse pointer-events-none"></div>
        
        {/* Header */}
        <div className="relative z-10 flex items-center justify-between p-5 border-b-2 border-cyan-500/30 bg-gradient-to-r from-slate-900/90 to-slate-800/90">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-lg">
              <Users size={24} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              My Community
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/30 hover:border-red-500/60"
          >
            <X size={22} className="text-red-400" />
          </button>
        </div>

        {/* Tabs with waveform-inspired design */}
        <div className="relative z-10 flex border-b-2 border-slate-700/50 bg-slate-900/80">
          {[
            { id: 'friends', label: 'Friends', count: friends.length },
            { id: 'following', label: 'Following', count: following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())).length },
            { id: 'artistFollowing', label: 'Artist Following', count: artistFollowing.length },
            { id: 'followers', label: 'Followers', count: followers.length },
            { id: 'search', label: 'Search', count: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-6 py-4 text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {/* Active tab waveform effect */}
              {activeTab === tab.id && (
                <div className={`absolute inset-0 bg-gradient-to-r ${tabColors[tab.id]} opacity-30`}></div>
              )}
              
              {/* Tab content */}
              <span className="relative z-10 flex items-center gap-2">
                {tab.label}
                {tab.count !== null && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    activeTab === tab.id 
                      ? 'bg-white/20 text-white' 
                      : 'bg-gray-700 text-gray-300'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </span>
              
              {/* Active tab bottom bar with gradient */}
              {activeTab === tab.id && (
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${tabColors[tab.id]}`}></div>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 overflow-y-auto overflow-x-hidden p-5 bg-slate-900/50">
          {activeTab === 'friends' && renderDockList(friends, true)}
          {activeTab === 'following' && renderDockList(following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())))}
          {activeTab === 'artistFollowing' && (
            artistFollowing.length === 0 ? (
              <div className="text-center py-12">
                <Bell size={48} className="mx-auto text-amber-400/30 mb-4" />
                <p className="text-gray-400 mb-2">Not following any artists yet</p>
                <p className="text-gray-500 text-sm">Follow DJs, Producers, and Streamers to see them here</p>
              </div>
            ) : (
              <div 
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-12 px-8 py-12"
                style={{ overflow: 'visible' }}
              >
                {artistFollowing.map((artist, index) => {
                  const scale = calculateScale(index, hoveredIndex);
                  const baseSize = 80;
                  
                  return (
                    <div
                      key={artist.id}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className="flex flex-col items-center transition-all duration-300 ease-out"
                      style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'center bottom',
                        zIndex: hoveredIndex === index ? 10 : 1,
                        // Fixed height to prevent layout shift
                        minHeight: '160px',
                      }}
                    >
                      {/* Avatar */}
                      <div
                        onClick={() => {
                          navigate(`/profile/${artist.id}`);
                          onClose();
                        }}
                        className="rounded-full flex items-center justify-center text-white font-bold shadow-2xl border-4 border-amber-500/30 hover:border-amber-400/50 transition-all duration-300 cursor-pointer overflow-hidden"
                        style={{
                          width: `${baseSize}px`,
                          height: `${baseSize}px`,
                          background: artist.profile_picture ? 'transparent' : (mockAvatars[index % mockAvatars.length] || 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'),
                          boxShadow: hoveredIndex === index 
                            ? '0 20px 40px rgba(251, 191, 36, 0.4), 0 0 20px rgba(251, 191, 36, 0.3)' 
                            : '0 10px 20px rgba(0, 0, 0, 0.5)',
                        }}
                      >
                        {artist.profile_picture ? (
                          <img 
                            src={`${API.replace('/api', '')}${artist.profile_picture.startsWith('/api') ? artist.profile_picture : '/api' + artist.profile_picture.replace('/uploads/', '/uploads/')}`}
                            alt={artist.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.style.background = mockAvatars[index % mockAvatars.length] || 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)';
                              e.target.parentElement.innerHTML = artist.name?.charAt(0).toUpperCase() || '?';
                            }}
                          />
                        ) : (
                          artist.name?.charAt(0).toUpperCase() || '?'
                        )}
                      </div>
                      
                      {/* Username */}
                      <div 
                        className="mt-2 text-white font-semibold text-center transition-all duration-300"
                        style={{
                          fontSize: hoveredIndex === index ? '14px' : '12px',
                          opacity: scale > 1 ? 1 : 0.8,
                        }}
                      >
                        {artist.name}
                      </div>
                      
                      {/* Artist type badge - always visible */}
                      <div className="text-xs text-amber-400 text-center capitalize">
                        {artist.user_type}
                      </div>
                      
                      {/* Bell placeholder - fixed height to prevent layout shift */}
                      <div 
                        style={{ height: '32px', marginTop: '8px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            opacity: hoveredIndex === index ? 1 : 0,
                            pointerEvents: hoveredIndex === index ? 'auto' : 'none',
                            transition: 'opacity 0.2s ease',
                          }}
                        >
                          <ArtistNotificationBell 
                            artistName={artist.name}
                            artistId={artist.id}
                            source="beatseek"
                            artworkUrl={artist.profile_picture ? `${API.replace('/api', '')}${artist.profile_picture.startsWith('/api') ? artist.profile_picture : '/api' + artist.profile_picture.replace('/uploads/', '/uploads/')}` : null}
                            size="medium"
                            showEmailAlert={false}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
          {activeTab === 'followers' && renderDockList(followers)}
          
          {activeTab === 'search' && (
            <div>
              <div className="mb-6">
                <div className="relative max-w-2xl mx-auto">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400" size={20} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search your community by name or email..."
                    className="w-full pl-12 pr-12 py-4 bg-slate-800/70 border-2 border-purple-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/60 transition-colors text-lg"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
                {searchQuery.trim() && (
                  <p className="text-center text-gray-400 text-sm mt-2">
                    {filteredFriends.length} result{filteredFriends.length !== 1 ? 's' : ''} found
                  </p>
                )}
              </div>
              
              {searchQuery.trim() ? (
                filteredFriends.length > 0 ? (
                  renderDockList(filteredFriends, true)
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <Search size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No results found for "{searchQuery}"</p>
                    <p className="text-sm mt-2">Try searching with a different name or email</p>
                  </div>
                )
              ) : (
                <div className="text-center py-16 text-gray-400">
                  <Search size={64} className="mx-auto mb-4 opacity-20" />
                  <p className="text-xl mb-2">Search Your Community</p>
                  <p className="text-sm">Start typing to find friends, following, and followers</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendsModal;
